import { localizedStrings } from '@core/strings'
import React, { useMemo } from 'react'
import {
  NavigationAwareTabs,
  ProjectLocationPicker,
  ProjectPicker,
  ProjectUserPicker,
  TaskCard,
} from '@elements'
import { useFormContext } from 'react-hook-form'
import { Button, Card } from '@common/material'
import { TemplateFormType } from '@modules/template-form/types'
import { Flex, Text } from '@fundamentals'
import { Section } from '@common/Section'
import { BriefingParticipantsSummary } from '@modules/briefings'
import moment from 'moment-timezone'
import { useGetProjectPlan } from '@core/react-query/features/projects/plans'
import { ProjectPlanPermitsModal } from '../projectPlan/modals/ProjectPlanPermitsModal'
import { useModal } from '@common/GlobalModal'
import Map from '@mui/icons-material/Map'
import { RegisterItemPicker } from '@elements/Pickers/RegisterItemPicker'
import {
  ProjectMapGeoDrawAreaCard,
  ProjectMapGeoDrawAreaModal,
  W3WMapCard,
} from '@modules/geolocation'
import { InspectionQuestionsStats } from './InspectionQuestionsStats'
import { TemplateFormAnswers } from '@core/entities/template/TemplateFormAnswers'
import { LinkedTaskResult } from '@core/entities/linkedProcesses'
import { User } from '@core/entities/user'
import { VirtualizedQuestionGroupList } from '@elements/Questions/VirtualizedQuestionGroupList'

type ReviewFormProps = TemplateFormType & {
  organisationId: number
  questionsFormKey?: string
}

const ReviewForm: React.FC<ReviewFormProps> = ({
  organisationId,
  TemplatePicker,
  dateEnabled,
  w3wEnabled,
  isUsedForInspections: isUsedForInspectionsProp,
  questionsFormKey = 'questions',
}) => {
  const { showModal } = useModal()
  const { getValues } = useFormContext()
  const {
    projectId,
    summary,
    startsOn,
    endsOn,
    template,
    registerId,
    registerItemId,
    [questionsFormKey]: questions,
    projectLocations,
    geoLocationAreas,
    coordinates,
    permitHolderId,
    permitHolder,
    registerTemplateInspectionId,
    registerInspection,
    briefingSession,
  } = useMemo(() => getValues(), [getValues])

  const briefingParticipants = (briefingSession?.participants ?? []) as Array<{
    firstName: string
    lastName: string
    company: string
    roleId: number
  }>

  const usedForInspections = template?.usedForInspections
  const stats = TemplateFormAnswers.getInspectionStats(questions || [])
  const raisedTasks = TemplateFormAnswers.getRaisedTasks(questions || [])

  const locationPlanId =
    projectLocations?.locations?.[0]?.locationPlanId ||
    projectLocations?.areas?.[0]?.locationPlan?.id ||
    projectLocations?.areas?.[0]?.locationPlanId ||
    projectLocations?.pins?.[0]?.planId ||
    projectLocations?.pins?.[0]?.locationPlanId

  const { data: projectPlan } = useGetProjectPlan(
    {
      projectId,
      nodeId: projectLocations?.locations?.[0]?.locationNodeId,
      planId: locationPlanId,
    },
    {
      enabled: !!projectLocations?.areas?.length,
    },
  )

  // The basic-information card stays in normal page flow (the window scroller
  // treats it as the list header); only the read-only question groups virtualize.
  const answers = (
    <VirtualizedQuestionGroupList
      questionGroups={questions ?? []}
      organisationId={organisationId}
      questionsFormKey={questionsFormKey}
      projectId={projectId}
      readOnly
    />
  )

  const tasks =
    raisedTasks?.map((task, index) => {
      return (
        <TaskCard
          key={index}
          task={
            {
              ...task,
              assignee: User.new(task.assigneeUser),
            } as unknown as LinkedTaskResult
          }
          sx={{ mb: 2 }}
        />
      )
    }) || []

  const tabs = [
    {
      label: localizedStrings.questions,
      component: answers,
    },
    {
      label: localizedStrings.tasks,
      component: tasks,
    },
  ]

  return (
    <>
      {projectId && template && (
        <Card sx={{ p: 2, mb: 2 }}>
          <ProjectPicker
            organisationId={organisationId}
            value={projectId}
            textFieldProps={{ fullWidth: true }}
            readOnly
          />
          <TemplatePicker
            organisationId={organisationId}
            projectId={projectId}
            value={template}
            sx={{ mt: 2 }}
            fullWidth
            fromRegisterInspection={!!registerTemplateInspectionId}
            isUsedForInspections={isUsedForInspectionsProp}
            readOnly
          />
          {registerId && (
            <RegisterItemPicker
              sx={{ mt: 2 }}
              registerId={registerId}
              value={registerItemId}
              fromInspection={!!registerTemplateInspectionId}
              fullWidth
              readOnly
            />
          )}
          {permitHolderId && (
            <ProjectUserPicker
              projectId={projectId}
              permitHolder={permitHolder}
              sx={{ mt: 2 }}
              value={permitHolderId}
              fullWidth
              readOnly
            />
          )}
          {projectLocations?.locations && (
            <ProjectLocationPicker
              projectId={projectId}
              sx={{ mt: 2 }}
              value={projectLocations.locations}
              readOnly
            />
          )}

          <Flex mt={1}>
            {geoLocationAreas && (
              <ProjectMapGeoDrawAreaCard
                projectId={projectId}
                geoLocationAreas={geoLocationAreas}
                readOnly
                onClick={() => {
                  showModal(
                    <ProjectMapGeoDrawAreaModal
                      projectId={projectId}
                      organisationId={organisationId}
                      initialGeoLocationAreas={geoLocationAreas}
                      readonly
                    />,
                  )
                }}
              />
            )}

            {(!!projectLocations?.areas?.length ||
              !!projectLocations?.pins?.length) &&
              projectPlan && (
                <Button
                  startIcon={<Map />}
                  onClick={() => {
                    showModal(
                      <ProjectPlanPermitsModal
                        nodeId={
                          projectLocations?.locations?.[0]?.locationNodeId
                        }
                        projectId={projectId}
                        planId={locationPlanId}
                        nodePath={
                          projectLocations?.locations?.[0]?.locationName
                        }
                        initialSelectedAreas={
                          projectLocations.areas?.[0]?.selectedAreas?.map(
                            ({ uuid }) => uuid,
                          ) || []
                        }
                        initialSelectedPins={
                          projectLocations.pins?.[0]?.droppedPins ?? []
                        }
                        plan={projectPlan}
                        readOnly
                      />,
                    )
                  }}
                >
                  <Text>
                    {projectPlan.getHumanReadableValueString(
                      projectLocations?.areas?.[0]?.selectedAreas?.map(
                        ({ uuid }) => uuid,
                      ) || [],
                      projectLocations?.pins?.[0]?.droppedPins ?? [],
                    )}
                  </Text>
                </Button>
              )}
          </Flex>

          {w3wEnabled && coordinates?.w3wLocation && (
            <W3WMapCard
              readOnly
              what3words={coordinates.w3wLocation}
              onClick={() => {}}
            />
          )}

          {dateEnabled && (
            <Flex mt={2}>
              <Section sx={{ mr: 4 }} label={localizedStrings.startDate}>
                {startsOn && (
                  <Text>{moment(startsOn).format('DD MMM YYYY HH:mm')}</Text>
                )}
              </Section>
              <Section label={localizedStrings.endDate}>
                {!!endsOn && (
                  <Text>{moment(endsOn).format('DD MMM YYYY HH:mm')}</Text>
                )}
              </Section>
            </Flex>
          )}

          {summary && (
            <Section sx={{ mt: 2 }} label={localizedStrings.summary}>
              <Text>{summary}</Text>
            </Section>
          )}

          {registerInspection?.expiration?.nextInspectionDue && (
            <Section
              sx={{ mt: 2 }}
              label={localizedStrings.inspectionExiryDate}
            >
              <Text>
                {moment(registerInspection.expiration.nextInspectionDue).format(
                  'DD MMM YYYY HH:mm',
                )}
              </Text>
            </Section>
          )}
          {usedForInspections && <InspectionQuestionsStats stats={stats} />}
        </Card>
      )}
      {stats.raisedTasks.total > 0 ? (
        <NavigationAwareTabs tabs={tabs} withoutQueryParam />
      ) : (
        answers
      )}

      <BriefingParticipantsSummary
        participants={briefingParticipants}
        templateId={template?.id}
        projectId={projectId}
      />
    </>
  )
}

export { ReviewForm }
